import type { DataImport } from "@/schema/data-import"
import { useQuery, type UseQueryOptions } from "@tanstack/react-query"

import { isAllBooksId } from "@/lib/all-books"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  fetchBusinessPreview,
  fetchDataImport,
  fetchDataImportProgress,
  fetchDataImports,
  fetchFilterOptions,
  fetchHouseholdPreview,
  fetchReviewItems,
} from "./api"

export const dataImportsQueryKeys = {
  all: ["data-imports"] as const,
  list: (bookId?: string) => [...dataImportsQueryKeys.all, bookId] as const,
  detail: (id: string, bookId?: string) => ["data-import", id, bookId] as const,
  progress: (id: string, bookId?: string) => ["data-import", id, "progress", bookId] as const,
  filterOptions: (id: string, bookId?: string) => ["data-import", id, "filter-options", bookId] as const,
  reviewItems: (id: string, bookId?: string) => ["data-import", id, "review-items", bookId] as const,
  householdPreview: (dataImportId: string, householdId: number, bookId?: string) =>
    ["data-import", dataImportId, "household", householdId, bookId] as const,
  businessPreview: (dataImportId: string, businessId: number, bookId?: string) =>
    ["data-import", dataImportId, "business", businessId, bookId] as const,
}

export function useFetchDataImports(
  optionsOrBookId?:
    | string
    | { bookId?: string; refetchInterval?: UseQueryOptions<DataImport[]>["refetchInterval"] }
    | null
) {
  const urlBookId = useBookId()
  const options = typeof optionsOrBookId === "object" && optionsOrBookId !== null ? optionsOrBookId : {}
  const bookIdOverride = typeof optionsOrBookId === "string" ? optionsOrBookId : options.bookId
  const bookId = bookIdOverride ?? urlBookId
  const getContext = useApiContext(bookId)

  return useQuery({
    queryKey: dataImportsQueryKeys.list(bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchDataImports(ctx)
    },
    // The endpoint rejects all-books mode with a 400, so don't query (or poll) there
    enabled: !isAllBooksId(bookId),
    refetchInterval: options?.refetchInterval,
    retry: false,
  })
}

export function useFetchDataImport(id: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.detail(id, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchDataImport(ctx, id)
    },
    enabled: !!id,
    retry: false,
  })
}

export function useFetchDataImportProgress(id: string, options?: { refetchInterval?: number; bookId?: string }) {
  const getContext = useApiContext(options?.bookId)
  const urlBookId = useBookId()
  const bookId = options?.bookId ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.progress(id, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchDataImportProgress(ctx, id)
    },
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
    retry: false,
  })
}

export function useFetchFilterOptions(dataImportId: string, options?: { bookId?: string }) {
  const getContext = useApiContext(options?.bookId)
  const urlBookId = useBookId()
  const bookId = options?.bookId ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.filterOptions(dataImportId, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchFilterOptions(ctx, dataImportId)
    },
    enabled: !!dataImportId,
    retry: false,
  })
}

export function useFetchReviewItems(dataImportId: string, options?: { enabled?: boolean; bookId?: string }) {
  const getContext = useApiContext(options?.bookId)
  const urlBookId = useBookId()
  const bookId = options?.bookId ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.reviewItems(dataImportId, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchReviewItems(ctx, dataImportId)
    },
    enabled: options?.enabled !== false && !!dataImportId,
    retry: false,
  })
}

export function useFetchHouseholdPreview(
  dataImportId: string,
  householdId: number,
  options?: { enabled?: boolean; bookId?: string }
) {
  const getContext = useApiContext(options?.bookId)
  const urlBookId = useBookId()
  const bookId = options?.bookId ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.householdPreview(dataImportId, householdId, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchHouseholdPreview(ctx, dataImportId, householdId)
    },
    enabled: (options?.enabled ?? true) && !!dataImportId && !!householdId,
    retry: false,
  })
}

export function useFetchBusinessPreview(
  dataImportId: string,
  businessId: number,
  options?: { enabled?: boolean; bookId?: string }
) {
  const getContext = useApiContext(options?.bookId)
  const urlBookId = useBookId()
  const bookId = options?.bookId ?? urlBookId

  return useQuery({
    queryKey: dataImportsQueryKeys.businessPreview(dataImportId, businessId, bookId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchBusinessPreview(ctx, dataImportId, businessId)
    },
    enabled: (options?.enabled ?? true) && !!dataImportId && !!businessId,
    retry: false,
  })
}
